import React, { memo, useCallback, useMemo, useState } from 'react';
import { VirtualizedList } from 'react-native';
import styled from 'styled-components/native';

import { ICountryDetail } from '@webtrader/core/api/account/country';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { getDialCodeFromCountryKey, setCountryKeyString } from '@webtrader/core/format/country';
import { useTranslation } from '@webtrader/core/i18n';
import { useCountry } from '@webtrader/core/services/country';
import { useTheme } from '@webtrader/core/services/theme/v2';

import ActionSheetSelector from '@/components/ActionSheet/Selectors';
import FeatherIcons from '@/components/Icons/Feather';
import Modal from '@/components/Modal';
import TextInput from '@/components/TextInput';

import { StyledActionSheet } from '../../screens/auth/DemoCreation/styled';
import CountrySelector from './Selector';

interface ICountryDialCodeDropdownProps {
  value: string | undefined; // countryKey
  withBorder?: boolean;
  onChange: ((value: string | number | undefined) => void) | undefined;
}

const CountryDialCodeDropdown: FCC<ICountryDialCodeDropdownProps> = (props) => {
  const { value, withBorder = true, onChange } = props;
  const { countryDetails } = useCountry();
  const { t } = useTranslation();
  const { theme } = useTheme();

  const [keyword, setKeyword] = useState('');
  const [showOptions, setShowOptions] = useState(false);

  const onOpen = () => setShowOptions(true);
  const onClose = () => setShowOptions(false);

  const onSelectCountry = useCallback(
    (countryKey: string) => {
      onChange?.(countryKey);

      onClose();
      setKeyword('');
    },
    [onChange]
  );

  const countryOptions = useMemo(() => {
    return countryDetails.filter(({ country }) =>
      country.replace(' ', '').toLowerCase().includes(keyword.replace(' ', '').toLowerCase())
    );
  }, [keyword, countryDetails]);

  return (
    <>
      <StyledActionSheet onPress={onOpen}>
        <ActionSheetSelector
          label={getDialCodeFromCountryKey(value)}
          withBorder={withBorder}
          testID={EDataTestId.DEMO_ACCOUNT_CREATION_MODAL_DIAL_CODE}
        />
      </StyledActionSheet>
      <Modal title={t('Dial Code')} visible={showOptions} onClose={onClose}>
        <ModalContentSearch>
          <TextInput
            value={keyword}
            prefix={<FeatherIcons name="search" size={18} color={theme.colors.text[2]} />}
            placeholder="Search"
            onChangeText={setKeyword}
            testID={EDataTestId.COUNTRY_DIAL_CODE_SEARCH}
          />
        </ModalContentSearch>
        <VirtualizedList<ICountryDetail>
          showsVerticalScrollIndicator={false}
          data={countryOptions}
          getItem={(data, i) => data[i]}
          getItemCount={() => countryOptions.length}
          keyExtractor={(item) => setCountryKeyString(item)}
          initialNumToRender={30}
          renderItem={({ item }) => {
            return (
              <CountrySelector
                country={item}
                selected={value === setCountryKeyString(item)}
                onSelect={onSelectCountry}
              />
            );
          }}
        />
      </Modal>
    </>
  );
};

export default memo(CountryDialCodeDropdown);

const ModalContentSearch = styled.View`
  padding: ${dynamicSize.convertSize(10)}px;
`;
